import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import cx from 'classnames';
import _ from 'lodash';
import CreatableSelect from 'react-select/creatable';
import { IKImage } from 'imagekitio-react';
import Truncate from "react-truncate";
import moment from "moment";
import Tooltip from "components/Tooltip";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { toast } from "react-toastify";
import StoryboardContext from 'components/Storyboard/context';
import { MediaLibraryModal } from 'components/MediaLibrary';

import { updatePageVisibility, deletePage } from "actions/pages";

import { makeHomePageForStoryboard } from "actions/storyboard"
import { createPageAliases, deletePageAlias, fetchPageAliases, invalidateAliases, updatePageAliases } from "actions/aliases";
import { createNewVersion, invalidateCreateState } from "actions/versions";
import { switchBreakpoint } from "actions/editor";
import { FailureToast, SuccessToast } from "components/Toast"

import {
  Row,
  Col,
  Overlay,
  OverlayTrigger,
  Popover,
  Image,
  Form,
  Container,
  Modal,
  Button,
  InputGroup,
  ProgressBar,
  Accordion,
  FormCheck,
  Badge
} from "react-bootstrap";

import { CopyEmbedCodeButton } from "components/Buttons";
import InfiniteScroll from "components/InfiniteScroll";
import storyboardRoleService from "services/storyboardRoles.service";
import { isIcV2 } from "../preview/icV2/helper";
import { MIXPANEL_PUBLISH_STORYBOARD, MIXPANEL_DISTRIBUTION_CLICKED } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { getStoryboardMixpanelEventPayload, getStoryboardType } from "services/mixpanelUtil";
import { socialShareOptions, socialShareTypes } from "constants/socialPublish";
import { updatePageHealthScoreCanonicalURL } from "actions/pageHealth";
import { MULTIPAGE_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import { useSortable } from "@dnd-kit/sortable";

import styles from './components.module.scss';

export const PageItem = ({ page, pages, active, onClick, onClone, onDelete, draggable, listeners, isOver, isActiveItem }) => {
  const dispatch = useDispatch();

  const pageId = _.get(page, 'id', null)
  const hidden = _.get(page, 'hidden', false)
  const published = _.get(page, 'published')
  const changes = _.get(page, 'changes')

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const homePage = _.get(storyboard, 'page', null);
  const storyboardType = _.get(homePage, 'storyboardType', null);

  const isHomePage = (id) => {
    return homePage && homePage.id === id;
  }

  const [deleteModal, showDeleteModal] = useState(false)

  const handlePageVisibility = (e) => {
    e.stopPropagation();
    document.body.click();
    dispatch(updatePageVisibility(pageId, !hidden))
  }

  const handleDelete = (e) => {
    e.stopPropagation();
    document.body.click();
    showDeleteModal(true)
  }

  const handlePageClone = (e) => {
    e.stopPropagation();
    document.body.click();
    onClone(pageId)
  }

  const handleSetHomePage = (e) => {
    e.stopPropagation();
    if (isHomePage(pageId)) {
      return;
    }
    document.body.click();
    dispatch(makeHomePageForStoryboard(storyboard.id, pageId))
  }
  const visiblePages = pages.filter(item => !item.hidden); 
  const canHideOrDelete = hidden || (visiblePages.length > 1 && !hidden)

  const popover = (
    <Popover id="popover-page-action" className={styles.pageSettingsPopover}>
      <Popover.Body className={styles.pageSettingsPopoverBody}>
        <div className={styles.pageSettingsPopoverContent}>
          <span onClick={handlePageClone}>Clone</span>
          <HideAction
            storyboardType={storyboardType}
            handlePageVisibility={handlePageVisibility}
            hidden={hidden}
            homePage={homePage}
            pageId={pageId}
            showButton={canHideOrDelete}
          />
          {pages.length > 1 && canHideOrDelete && storyboardRoleService.canManageStoryboard(storyboard) && <span onClick={handleDelete}>Delete</span>}
          {!isHomePage(pageId) && !hidden && !MULTIPAGE_STORYBOARDS.includes(storyboardType) && <span onClick={handleSetHomePage}>Set as Home</span>}
        </div>
      </Popover.Body>
    </Popover>
  );

  const handlePageClick = (e) => {
    e.stopPropagation();
    document.body.click();
    onClick(page);
  }

  const handleOverlay = (e) => {
    e.stopPropagation();
    document.body.click();
  }

  return (
    <div className={cx(styles.pageItem, { [styles.selected]: active, [styles.hover]: isOver, [styles.active]: isActiveItem })} onClick={handlePageClick}>
    {(isHomePage(page.id) && !MULTIPAGE_STORYBOARDS.includes(storyboardType)) && <i className={cx('cleverstory-home', styles.home)}></i>}
      {draggable && <Handle listeners={listeners} />}
      <TruncatedTextWithTooltip placement="top" className={styles.name} content={page.name} maxWidth={130}>
          {page.name}
      </TruncatedTextWithTooltip>
      <div className={styles.tags}>
        {published &&
          <label className={cx(styles.status, styles.live)}>LIVE</label>
        }
        {(changes || !published) &&
          <label className={cx(styles.status, styles.draft)}>DRAFT</label>
        }
      </div>
      {hidden &&
        <i className={cx('cleverstory-hide', styles.iconHide)}></i>
      }

      {
        <OverlayTrigger
          rootClose={true}
          trigger="click"
          placement="right"
          overlay={popover}
        >
          <i className={cx('cleverstory-more', styles.iconMore)} onClick={handleOverlay} />
        </OverlayTrigger>

      }

      <DeleteModal deleteModal={deleteModal}
        showDeleteModal={showDeleteModal}
        page={page}
        pages={pages}
        onDelete={onDelete} />

    </div>
  )
}
export const SortablePageItem = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
  } = useSortable({ id: props.id });

  return (
    <div ref={setNodeRef} {...attributes}>
      <PageItem
        {...props}
        listeners={listeners}
      />
    </div>
  );
};
export const CreateVersionModal = ({ storyboard, show, onDiscard }) => {

  const dispatch = useDispatch();

  const storyboardName = _.get(storyboard, 'name');
  const createdState = useSelector((state) => _.get(state, `versions.createdState`));

  const [versionName, setVersionName] = useState('');

  useEffect(()=>{
    if(show){
      setVersionName(storyboardName)
    }
  },[show])

  useEffect(()=>{
    if(!createdState){
      return
    }

    if (createdState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`Version Created Successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      dispatch(invalidateCreateState());
    }else if (createdState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Maximum of 10 version reached, delete old ones to save new.`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 5000,
        }
      );
      dispatch(invalidateCreateState());
    }

    onDiscard();
  },[createdState])

  const handleChange = (e) => {
    let { value } = e.target;
    setVersionName(value);
  }

  const handleSave = () => {
    if(_.isEmpty(versionName)){
      return
    }

    let request = {
      name: versionName,
      storyboardId: storyboard.id,
      published: false
    }
    dispatch(createNewVersion(request));
  }

  return (
    <Modal backdrop='static'
      className={styles.addNewVersionModal}
      contentClassName={styles.newVersionContent}
      dialogClassName={styles.newVersionDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <button className={styles.closeButton} onClick={onDiscard}>
          <i className={cx('cleverstory-close', styles.closeButtonIcon)} />
      </button>
      <Modal.Body className={styles.newVersionBody}>
        <Row className={styles.messageRow}>
          <Col className={styles.messageContainer} xs={6}>
            <div>
              <h2 className={styles.messageTitle}>Mark this version as a moment in time?</h2>
              <p className={styles.messageDescription}> Version creation may take some time. Please ensure no one else is using the same editor during this process to prevent conflicts.</p>
              <Form.Group className={styles.inputForm}>
                <Form.Label className={styles.formLabel}>Version Name</Form.Label>
                <InputGroup className="mb-3">
                  <Form.Control className={styles.formInput} onChange={handleChange} value={versionName}/>
                </InputGroup>
              </Form.Group>
            </div>
            <footer className={styles.saveChangeButtons}>
              <Button variant="primary" onClick={handleSave}>Save Version</Button>
              <Button variant="secondary" onClick={onDiscard}>Cancel</Button>
            </footer>
          </Col>
          <Col className={styles.imageContainer} xs={6}>
            <Image src={'/images/versioning.png'} className={styles.versionImage} />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export const DeleteModal = ({ deleteModal, showDeleteModal, onDelete, page }) => {

  const id = _.get(page, 'id', null)
  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const currentPage = pages && pages.find(page => page.id === id)
  const deleting = useSelector(state => _.get(state, 'pages.deletion.deleting', false));
  const deleted = useSelector(state => _.get(state, 'pages.deletion.deleted', false));

  const handleDeleteClick = (e) => {
    onDelete(id);
    e.stopPropagation()
  }

  useEffect(() => {
    if (deleted) {
      showDeleteModal(false)
    }
  }, [deleted])

  return (
    <Modal
      className={styles.deletePageModal}
      contentClassName={styles.deletePageModalContent}
      dialogClassName={styles.deletePageModalDialog}
      backdropClassName={styles.backdropWrapper}
      show={deleteModal}
      centered size="lg"
      onClick={(e) => { e.stopPropagation() }}
    >
      <Modal.Body className={styles.deletePageModalBody}>
        <div className={styles.detailsContainer}>
          <div className={styles.messageContainer} >
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <p className={styles.messageContent}>
              This action of yours will make the current URL inactive with a 404 page. You can republish it anytime from the storyboard editor.
            </p>
            <footer className={styles.deletePageModalButtons}>
              <Button variant="secondary" onClick={() => { showDeleteModal(false) }} >Cancel</Button>
              <Button variant="primary" onClick={handleDeleteClick} >{deleting ? 'Deleting...' : 'Delete'}</Button>
            </footer>
          </div>
          <div>
            <Image src={'/images/deletePage.png'} className={styles.deletePageModalImage} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export const SEOBasics = ({page, value, onChange }) => {
  const [nameFocused, setNameFocused] = useState(false);

  const isPageHidden = _.get(page, 'hidden', false)
  const pageName = _.get(value, 'info.name', '');
  const name = _.get(value, 'seo.name') || pageName;
  const description = _.get(value, 'seo.description') || '';
  const excludeFromSearch = _.get(value, 'seo.excludeFromSearch', false)
  const storyboardType = useSelector((state) => _.get(state, "storyboards.storyboard.item.type") || null);
  const keywords = (_.get(value, 'seo.keywords') || []).map(item => {
    return {
      label: item,
      value: item
    }
  });

  const handleNameChange = (e) => {
    const value = e.target.value;
    onChange({
      name: value
    });
  }

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    onChange({
      description: value
    });
  }

  const handleKeywordsChange = (data) => {
    const keywords = data.map(item => item.value);
    onChange({
      keywords: keywords
    });
  }

  const handleToggleExcludeFromSearch = (e) => {
    onChange({
      excludeFromSearch: !excludeFromSearch
    });
  }

  const LABELS = {
    [STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2]: {
      title: "Title Tag",
      description: "Meta Description",
      keywords: "Meta Tags",
    },
    default: {
      title: "Page Title Tag",
      description: "Page Meta Description",
      keywords: "Page Meta Tags",
    },
  };

  const currentLabels = LABELS[storyboardType] || LABELS.default;

  return (
    <>
      <div className={styles.seoBasics}>
        <h3 className={styles.header}>SEO Basics</h3>
        <Form className={styles.form}>
          <Form.Group controlId="pageTitle" className={styles.group}>
            <Form.Label className={styles.label}>{currentLabels.title}</Form.Label>
            <Form.Control
              className={styles.control}
              type="text"
              name="name"
              placeholder="Enter page title"
              value={nameFocused ? _.get(value, 'seo.name') : name}
              onChange={handleNameChange}
              onFocus={() => { setNameFocused(true) }}
              onBlur={() => { setNameFocused(false) }} />
          </Form.Group>
          <Form.Group controlId="pageDescription" className={styles.group}>
            <Form.Label className={styles.label}>{currentLabels.description}</Form.Label>
            <Form.Control
              as="textarea"
              className={styles.control}
              type="text"
              name="description"
              placeholder="Enter page description"
              value={description}
              onChange={handleDescriptionChange} />
          </Form.Group>
          <Form.Group controlId="PageMetaTags" className={styles.group}>
            <Form.Label className={styles.label}>{currentLabels.keywords}</Form.Label>
            <CreatableSelect
              className={styles.select}
              name="keywords"
              isMulti
              options={[]}
              value={keywords}
              onChange={handleKeywordsChange}
            />
          </Form.Group>
          {storyboardType !== STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && (
            <Form.Group controlId="excludeIndexing" className={cx(styles.smallGroup, styles.flexFormGroup)}>
              <Form.Label className={styles.inlineLabel}> Exclude from indexing </Form.Label>
              <Form.Check
                type="switch"
                className={styles.switchButton}
              >
                <FormCheck.Input isvalid onChange={handleToggleExcludeFromSearch} checked={excludeFromSearch} disabled={isPageHidden}/>
              </Form.Check>
            </Form.Group>
          )}
        </Form>
      </div>
    </>
  )
}

export const MultiplePageAlias = ({ editorSession, page, value, active }) => {
  const dispatch = useDispatch();

  const [selectedAlias, setSelectedAlias] = useState(null);
  const [selectedDomain,setSelectedDomain]= useState({});
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', null));
  const loading = useSelector((state) => _.get(state, "aliases.loading", false));
  const updating = useSelector(state => _.get(state, `pages.page.${page.id}.update.updating`, false));
  let aliases = useSelector((state) => _.get(state, "aliases.items", null)) || [];
  aliases = _.sortBy(aliases, 'createdDate').reverse();
  const pagination = useSelector((state) => _.get(state, "aliases.pagination"));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;
  const created = useSelector(state => _.get(state, 'aliases.alias.created'));
  const updated = useSelector(state => _.get(state, 'aliases.alias.updated'));
  const persistedItem = useSelector(state => _.get(state, 'aliases.alias.item'));
  const deleted = useSelector(state => _.get(state, 'aliases.alias.deleted'));

  const handleAddingAlias = () => {

    setSelectedAlias({
      type: 'page',
      target: _.get(page, 'id'),
      url: "",
      autoCreate: false,
      canonical: false,
      widget: ""
    })
  }

  useEffect(() => {
    const pageId = _.get(page, 'id') || null;
    
    if (!updating && pageId) {
      var fetchRequest = {
        type: 'page',
        target: pageId
      }

      dispatch(fetchPageAliases(fetchRequest, 0, 20));

      return () => {
        dispatch(invalidateAliases());
        setSelectedAlias(null)
      }
    }

  }, [page]);

  useEffect(()=>{
    let domainUrl = _.get(storyboard,'domain') || _.get(workspace, 'defaultDomain', null);
    setSelectedDomain(domainUrl);
  },[storyboard])

  useEffect(() => {
    if (selectedAlias || _.size(aliases) == 0) {
      return;
    }

    const selection = aliases[0];
    handleSelectedAlias(selection);

  }, [aliases]);

  useEffect(() => {
    if ((created || updated) && persistedItem) {
      handleSelectedAlias(persistedItem);
    }
  }, [created, updated]);

  useEffect(() => {
    if (deleted) {
      if (aliases.length == 0) {
        return;
      }

      const next = aliases[0];
      handleSelectedAlias(next);
    }
  }, [deleted])

  const handleSelectedAlias = (alias) => {
    if (alias) {
      setSelectedAlias(alias)
    }
  }

  const handleViewPage = (alias) => {
    const url = `${selectedDomain.url}${alias}`
    window.open(url);
  }

  const loadMoreAliases = () => {

    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    var fetchRequest = {
      type: 'page',
      target: _.get(page, 'id')
    }
    dispatch(fetchPageAliases(fetchRequest, nextPage, 20));
  }

  
  return (
    <div className={styles.manageAliasContainer}>
      <div className={styles.details}>
        <div className={styles.header}>
          <span className={styles.title}>Manage Aliases</span>
          {!SLIDE_STORYBOARDS.includes(storyboard?.type) &&
            <Button className={styles.manageButton} onClick={handleAddingAlias}>
              <i className={cx('cleverstory-plus', styles.icon)} />
              Add Alias
            </Button>
          }
        </div>
        <div className={styles.list}>
          {selectedAlias && !selectedAlias.id &&
            <AliasCards key='new_alias' selectedAlias={selectedAlias} data={selectedAlias} handleSelectedAlias={handleSelectedAlias} />
          }
        <InfiniteScroll className={styles.scrollContainer} loading={loading} hasMore={hasMoreItems} loadMore={loadMoreAliases}>
        {loading && _.size(aliases) === 0 &&
          <div className={styles.loading}>Loading aliases...</div>
        }
          {aliases.map((data, index) => {
            return (
              <AliasCards key={index} handleViewPage={handleViewPage} selectedAlias={selectedAlias} data={data} handleSelectedAlias={handleSelectedAlias} selectedDomain={selectedDomain}/>
            )
          })}
        </InfiniteScroll>
        </div>

      </div>
      <AliasEditor selectedAlias={selectedAlias} page={page} />
    </div>
  )
}

const AliasCards = ({ data, key, selectedAlias, handleSelectedAlias, handleViewPage, selectedDomain }) => {
  const createNew = data.id == null;
  const [tooltip, setTooltip] = useState(false);
  const domainUrl = selectedDomain ? selectedDomain.url : null;
  const getModifiedDate = () => {
    const currentDate = moment();

    if (!data.modifiedDate) {
      return moment().from(currentDate);
    }

    const providedDate = moment(data.modifiedDate);
    return providedDate.from(currentDate);
  }

  const handleTooltip = (truncated) => {
    if (truncated) {
      setTooltip(truncated);
    }
  }

  return (
    <div key={key} onClick={() => { handleSelectedAlias(data) }} className={cx(styles.aliasDetailContainer, { [styles.activeAlias]: _.get(selectedAlias, 'id') == data.id })}>
      <div className={styles.info}>
        <div className={styles.aliasFormat}>
          <span className={styles.date}>{getModifiedDate()}</span>
          {_.get(data, 'canonical', false) &&
            <Badge className={styles.canonicalBadge} >Canonical</Badge>
          }
        </div>
        <Tooltip placement="top" disabled={!tooltip} content={_.get(data, 'url')}>
          <span className={styles.aliasName}>{createNew ?
            'Untitled' :
            <Truncate onTruncate={handleTooltip} lines={1}>
              {_.get(data, 'url')}
            </Truncate>
          }
          </span>
        </Tooltip>
        <span className={styles.userInfo}>{createNew ? 'Created By User' : `created By: ${_.concat(_.get(data, 'createdBy.firstName', ''), ' ', _.get(data, 'createdBy.lastName', '')).join('')}`}</span>
      </div>
      <Tooltip placement="top" content={`${domainUrl}${_.get(data, 'url')}`}>
      <Button disabled={createNew} onClick={() => handleViewPage(_.get(data, 'url'))} variant="info">
        <i className={cx('cleverstory-url', styles.urlIcon)} />
      </Button>
      </Tooltip>
    </div>
  )
}

const AliasEditor = ({ selectedAlias, page }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    request: null,
    error: null,
    success: null
  });

  const createNew = !_.get(selectedAlias, 'id', '');
  const aliases = useSelector((state) => _.get(state, "aliases.items", null)) || [];
  const creating = useSelector(state => _.get(state, 'aliases.alias.creating'));
  const updating = useSelector(state => _.get(state, 'aliases.alias.updating'));

  const created = useSelector(state => _.get(state, 'aliases.alias.created'));
  const updated = useSelector(state => _.get(state, 'aliases.alias.updated'));

  const error = useSelector(state => _.get(state, 'aliases.alias.error.message'));
  const deleted = useSelector(state => _.get(state, 'aliases.alias.deleted'));
  const [deleteModal, showDeleteModal] = useState(false)

  const saving = creating || updating;

  useEffect(() => {
    if (selectedAlias) {
      setState((state) => {
        return {
          ...state,
          validation: null,
          request: {
            ...state.request,
            ...selectedAlias
          }
        }
      });
    }
  }, [selectedAlias]);

  useEffect(() => {
    if (!created && !updated) {
      return;
    }

    var message = null;
    if (created) {
      message = "Alias created successfully";
    } else if (updated) {
      message = "Alias updated successfully";
    }

    setState((state) => {
      return {
        ...state,
        error: error,
        success: message
      }
    });

    toast(
      <SuccessToast
        heading={`Success`}
        description={message}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      })

  }, [created, updated]);

  useEffect(() => {
    if (error) {
      let errorDescription = "Unable to update Alias";

      if (error === 'alias.duplicate') {
        errorDescription = "The entered Site URL already exists";
      }

      toast(
        <FailureToast
          heading={`Failure`}
          description={errorDescription}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

    }
  }, [error]);

  const validateForm = (request) => {
    const url = _.get(request, 'url');
    const autoCreate = _.get(request, 'autoCreate');
    var aliasRegex = /^(?!.*\/\/)[a-z_-]|[`\s!@#$%^&*()+\=\[\]{};':"\\|,.<>\?~]/;
    var slashRegext = /^\/([^\/])+(?:\/[^\/]+)*$/gm
    if (autoCreate || url==="/") {
      return null;
    }

    if (aliasRegex.test(url) || !slashRegext.test(url)) {
      return {
        url: "Please provide valid alias"
      };
    }

    if (!url || url.trim() == '') {
      return {
        url: "Alias cannot be empty"
      };
    }

    if (!url.startsWith('/')) {
      return {
        url: "Alias must start with /"
      }
    }
  }

  const handleDelete = () => {
    var deleteRequest = {
      type: page.type === 'INTERACTIVE' ? 'interactive' : 'page',
      target: _.get(page, 'id')
    }

    dispatch(deletePageAlias(_.get(selectedAlias, 'id'), deleteRequest))
  }

  const handleUpdateAlias = (type, value) => {
    setState((state) => {
      let updatedRequest = {
        ...state.request,
        [type]: value,
      };

      if (type === 'autoCreate' && value) {
        const currentUrl = _.get(state.request, 'url', '');
          updatedRequest.url = _.get(selectedAlias, 'url', currentUrl);
      }
  
      return {
        ...state,
        request: updatedRequest
      };
    });
  };

  const handleSave = () => {
    const error = validateForm(state.request);
    setState((state) => {
      return {
        ...state,
        validation: error
      }
    });

    if (error) {
      return;
    }

    var targetValue = _.get(state.request, 'target');

    if (typeof targetValue !== 'string'){
      targetValue = _.get(targetValue, 'id');
    }

    var request = {
      ...state.request,
      target: targetValue
    }

    if(request?.canonical) dispatch(updatePageHealthScoreCanonicalURL(request?.url || ""));

    if (selectedAlias.id == null) {
      dispatch(createPageAliases(request));
    } else {
      dispatch(updatePageAliases(selectedAlias.id, request));
    }
  }

  const handleResest = () => {
    setState((state) => {
      return {
        ...state,
        request: selectedAlias
      }
    })
  }

  return (
    <div className={styles.detailsEditor}>
      <div className={styles.manageAliasInfo}>
        <div className={styles.headerAction}>
          <span className={styles.title}>URL Name</span>
          <Button disabled={(_.get(selectedAlias, 'canonical', false)) || createNew} onClick={() => { showDeleteModal(true) }} variant="info">
            <i className={cx('cleverstory-trash', styles.deleteicon)} />
          </Button>
        </div>

        <Form.Group>
          <Form.Control className={styles.aliasInputField} 
            disabled={_.get(state.request, 'autoCreate', false)} 
            onChange={(e) => handleUpdateAlias('url', e.target.value)} 
            name='url' 
            value={_.get(state.request, 'url', "")}
          />

          {_.get(state.validation, 'url') &&
            <span className={styles.error}>{_.get(state.validation, 'url')}</span>
          }
          <Form.Check className={styles.autoGenerateCheckBox} checked={_.get(state.request, 'autoCreate', false)} onChange={(e) => handleUpdateAlias('autoCreate', e.target.checked)} name="autoCreate" type="checkbox" label="Auto generated alias" />
        </Form.Group>
      </div>
      <div className={styles.canonicalAction}>
        <div className={styles.switchAction}>
          <span className={styles.label}>Mark as canonical</span>
          <Form.Check
            type="switch"
            id="custom-switch"
            disabled={_.get(selectedAlias, 'canonical', false)}
            name="canonical"
            onChange={(e) => handleUpdateAlias("canonical", e.target.checked)}
            checked={_.get(state.request, 'canonical', false)}
            className={styles.toggleSwitch}
          />
        </div>
        <span className={styles.textInfo}>This alias will be used as a default url</span>
      </div>
      <footer className={styles.buttonContainer}>
        <Button onClick={handleResest} className={styles.actionButton} variant="secondary" type="reset">Reset</Button>
        <Button onClick={handleSave} variant="primary" type="submit">{saving ? 'saving...' : 'Save Changes'}</Button>
      </footer>
      <DeleteAliasesModal
        deleteModal={deleteModal}
        showDeleteModal={showDeleteModal}
        onDelete={handleDelete}
      />
    </div>
  )
}

export const DeleteAliasesModal = ({ deleteModal, showDeleteModal, onDelete, personalizationId, aliasId }) => {

  const deleting = useSelector(state => _.get(state, 'aliases.alias.deleting', false));
  const deleted = useSelector(state => _.get(state, 'aliases.alias.deleted', false));

  const handleDeleteClick = (e) => {
    onDelete();
    e.stopPropagation()
  }

  useEffect(() => {
    if (deleted) {
      showDeleteModal(false)
    }
  }, [deleted])

  return (
    <Modal
      className={styles.deletePageModal}
      contentClassName={styles.deletePageModalContent}
      dialogClassName={styles.deletePageModalDialog}
      backdropClassName={styles.backdropWrapper}
      show={deleteModal}
      centered size="lg"
      onClick={(e) => { e.stopPropagation() }}
    >
      <Modal.Body className={styles.deletePageModalBody}>
        <div className={styles.detailsContainer}>
          <div className={styles.messageContainer} >
            <h2 className={styles.messageTitle}>Are you sure?</h2>
            <p className={styles.messageContent}>
              This action of yours will make the current URL inactive with a 404 page.
            </p>
            <footer className={styles.deletePageModalButtons}>
              <Button variant="secondary" onClick={() => { showDeleteModal(false) }} >Cancel</Button>
              <Button variant="primary" {...(personalizationId && { 'data-track-props': 'Delete Personalized URL' })} 
                data-track-id={personalizationId} data-track-alias={aliasId}
                onClick={handleDeleteClick} >{deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </footer>
          </div>
          <div>
            <Image src={'/images/deletePage.png'} className={styles.deletePageModalImage} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}


export const SocialShare = ({ value, onChange }) => {
  const [state, setState] = useState({
    library: false,
    nameFocused: false,
    descriptionFocused: false
  });

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = useSelector((state) => _.get(state, "storyboards.storyboard.item.type") || null);
  const aliasPath = isIcV2(storyboardType) ? 'info.subDirectory' : 'info.alias';
  const pageName = _.get(value, 'info.name', '');
  const pageAlias = _.get(value, aliasPath, '');

  const seoName = _.get(value, 'seo.name') || pageName;
  const seoDescription = _.get(value, 'seo.description') || '';

  const name = _.get(value, 'social.name') || seoName;
  const description = _.get(value, 'social.description') || seoDescription;
  const icon = _.get(value, 'social.icon.url');

  const handleUploadLibrary = () => {
    setState(state => {
      return {
        ...state,
        library: true
      }
    });
  }

  const handleCloseMediaLibrary = () => {
    setState(state => {
      return {
        ...state,
        library: false
      }
    });
  }

  const handleCreateImage = (medias) => {
    const media = medias[0];
    onChange('social', {
      icon: media
    });

    handleCloseMediaLibrary();
  }

  const handleNameChange = (e) => {
    const value = e.target.value;
    onChange('social', {
      name: value
    });
  }

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    onChange('social', {
      description: value
    });
  }

  const LABELS = {
    [STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2]: {
      subHeader: "Social Preview",
      pageName: "Page Name",
      description: "Meta description",
    },
    default: {
      subHeader: "Preview on social",
      pageName: "What's this page name?",
      description: "Page Meta Tags",
    },
  };

  const currentLabels = LABELS[storyboardType] || LABELS.default;

  return (
    <>
      <div className={styles.socialShare}>
        <h3 className={styles.header}>Social Share Settings</h3>

        <p className={styles.subHeader}>{currentLabels.subHeader}</p>
        <section className={styles.preview}>
          <div className={styles.inner} onClick={handleUploadLibrary.bind(this, 'socialInfo')}>
            <div className={styles.image}>
              {icon &&
                <IKImage
                  path={icon}
                  transformation={[{
                    height: 400,
                    format: 'webp'
                  }]}
                />
              }
              <div className={styles.upload}>
                <i className={cx('cleverstory-upload', styles.icon)} />
                <span className={styles.lable}>Choose Media</span>
              </div>
            </div>

            <div className={styles.details}>
              <div className={styles.url}>
                {pageAlias}
              </div>
              <div className={styles.title}>{name}</div>
              <div className={styles.description}>
                {description}
              </div>
            </div>
          </div>
        </section>

        <Form className={styles.form}>
          <Form.Group controlId="pageTitle" className={styles.group}>
            <Form.Label className={styles.label}>{currentLabels.pageName}</Form.Label>
            <Form.Control
              className={styles.control}
              type="text"
              name="name"
              placeholder="Enter page title"
              value={state.nameFocused ? _.get(value, 'social.name') : name}
              onChange={handleNameChange}
              onFocus={() => {
                setState(state => {
                  return {
                    ...state,
                    nameFocused: true
                  }
                })
              }}
              onBlur={() => {
                setState(state => {
                  return {
                    ...state,
                    nameFocused: false
                  }
                })
              }} />
          </Form.Group>
          <Form.Group controlId="pageDescription" className={styles.group}>
            <Form.Label className={styles.label}>{currentLabels.description}</Form.Label>
            <Form.Control
              as="textarea"
              className={styles.control}
              type="text"
              name="description"
              placeholder="Enter page description"
              value={state.descriptionFocused ? _.get(value, 'social.description') : description}
              onChange={handleDescriptionChange}
              onFocus={() => {
                setState(state => {
                  return {
                    ...state,
                    descriptionFocused: true
                  }
                })
              }}
              onBlur={() => {
                setState(state => {
                  return {
                    ...state,
                    descriptionFocused: false
                  }
                })
              }} />
          </Form.Group>
        </Form>

        <Accordion className={styles.twitterAccordion}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Twitter Settings
            </Accordion.Header>
            <Accordion.Body>
              <TwitterSettings value={value} onChange={onChange} />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      <MediaLibraryModal
        show={state.library}
        storyboard={storyboardContext}
        multiple={false}
        onSelect={handleCreateImage}
        onClose={handleCloseMediaLibrary}
        mediaTarget={'social_share'}
      />
    </>
  )
}

export const TwitterSettings = ({ value, onChange }) => {
  const [state, setState] = useState({
    library: false,
    nameFocused: false,
    descriptionFocused: false
  });

  const storyboardContext = React.useContext(StoryboardContext);

  const pageName = _.get(value, 'info.name', '');
  const pageAlias = _.get(value, 'info.alias', '');

  const seoName = _.get(value, 'seo.name') || pageName;
  const seoDescription = _.get(value, 'seo.description') || '';

  const socialName = _.get(value, 'social.name') || seoName;
  const socialDescription = _.get(value, 'social.description') || seoDescription;
  const socialIcon = _.get(value, 'social.icon.url');

  const name = _.get(value, 'twitter.name') || socialName;
  const description = _.get(value, 'twitter.description') || socialDescription;
  const icon = _.get(value, 'twitter.icon.url') || socialIcon;
  const size = _.get(value, 'twitter.size') || 'large';

  const handleNameChange = (e) => {
    const value = e.target.value;
    onChange('twitter', {
      name: value
    });
  }

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    onChange('twitter', {
      description: value
    });
  }

  const handleCardTypeChange = (size) => {
    onChange('twitter', {
      size: size
    });
  }

  const handleUploadLibrary = () => {
    setState(state => {
      return {
        ...state,
        library: true
      }
    });
  }

  const handleCloseMediaLibrary = () => {
    setState(state => {
      return {
        ...state,
        library: false
      }
    });
  }

  const handleCreateImage = (medias) => {
    const media = medias[0];
    onChange('twitter', {
      icon: media
    });

    handleCloseMediaLibrary();
  }

  return (
    <div className={styles.socialShare}>
      <Form className={styles.form}>
        <Form.Group controlId="pageSize" className={cx(styles.group, styles.twitterSettings)}>
          <Form.Label className={styles.label}>Card size on twitter</Form.Label>
          <div className={styles.cardTypeWrapper}>
            <div className={cx(styles.cardType)} onClick={() => handleCardTypeChange('large')}>
              <input type="radio" checked={size === 'large'} onChange={() => handleCardTypeChange('large')}></input>
              <label>Large</label>
            </div>
            <div className={cx(styles.cardType)} onClick={() => handleCardTypeChange('small')}>
              <input type="radio" checked={size === 'small'} onChange={() => handleCardTypeChange('small')}></input>
              <label>Small</label>
            </div>
          </div>
        </Form.Group>
      </Form>
      <p className={styles.subHeader}>Preview on twitter</p>

      <section className={cx(styles.preview, styles.twitter, {
        [styles.small]: size == 'small'
      }
      )}>
        <div className={styles.inner} onClick={handleUploadLibrary}>
          <div className={styles.image}>
            {icon &&
              <IKImage
                path={icon}
                transformation={[{
                  height: 400,
                  format: 'webp'
                }]}
              />
            }
            <div className={styles.upload}>
              <i className={cx('cleverstory-upload', styles.icon)} />
              <span className={styles.label}>Choose Media</span>
            </div>
          </div>

          <div className={styles.details}>
            <div className={styles.url}>
              {pageAlias}
            </div>
            <div className={styles.title}>
              {name}
            </div>
            <div className={styles.description}>
              {description}
            </div>
          </div>
        </div>
      </section>

      <Form className={styles.form}>
        <Form.Group controlId="pageTitle" className={styles.group}>
          <Form.Label className={styles.label}>What’s this page name?</Form.Label>
          <Form.Control
            className={styles.control}
            type="text"
            placeholder="Enter page title"
            name="name"
            value={state.nameFocused ? _.get(value, 'twitter.name') : name}
            onChange={handleNameChange}
            onFocus={() => {
              setState(state => {
                return {
                  ...state,
                  nameFocused: true
                }
              })
            }}
            onBlur={() => {
              setState(state => {
                return {
                  ...state,
                  nameFocused: false
                }
              })
            }} />
        </Form.Group>
        <Form.Group controlId="pageDescription" className={styles.group}>
          <Form.Label className={styles.label}>Page Meta description</Form.Label>
          <Form.Control
            as="textarea"
            className={styles.control}
            type="text"
            name="description"
            placeholder="Enter page description"
            value={state.descriptionFocused ? _.get(value, 'twitter.description') : description}
            onChange={handleDescriptionChange}
            onFocus={() => {
              setState(state => {
                return {
                  ...state,
                  descriptionFocused: true
                }
              })
            }}
            onBlur={() => {
              setState(state => {
                return {
                  ...state,
                  descriptionFocused: false
                }
              })
            }} />
        </Form.Group>
      </Form>
      <MediaLibraryModal
        show={state.library}
        storyboard={storyboardContext}
        multiple={false}
        onSelect={handleCreateImage}
        onClose={handleCloseMediaLibrary}
        mediaTarget={'social_share'}
      />
    </div>
  )
}

export const SaveMessageModal = ({ show, onSave, onDiscard }) => {
  return (
    <Modal backdrop='static'
      className={styles.saveChangeModal}
      contentClassName={styles.saveChangeContent}
      dialogClassName={styles.saveChangeDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.saveChangeBody}>
        <Image src={'/images/saveChanges.png'} className={styles.saveMessageImage} />
        <Row className={styles.messageRow}>
          <Col className={styles.messageContainer} xs={{ span: 6, offset: 6 }}>
            <h2 className={styles.messageTitle}>Wait, what?</h2>
            <span className={styles.messageContent}>
              Changes made to this mission<br /> haven’t been saved. Sure you<br /> want to continue?
            </span>
            <footer className={styles.saveChangeButtons}>
              <Button variant="secondary" onClick={onDiscard} >Discard</Button>
              <Button variant="primary" onClick={onSave} >Save Changes</Button>
            </footer>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export const PublishLoader = ({editorSession, show, onDiscard, passwordProtected, percentage, totalPages, siteUrl, domain, pageId, hasVariableMappings }) => {

  const dispatch = useDispatch();
  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const storyboard = useSelector(state => _.get(state, 'storyboards.storyboard.item', {}));
  const layoutChangeIndicator = useSelector(state => _.get(state, `${editorSession}.present.layoutIndicator`));

  var completed = totalPages.filter((data) => data.published);
  var errorPage = totalPages.filter((data) => data.error);
  let aliasurl = useSelector(state => _.get(state, `pages.page.${pageId}.publish.error`, null));
  let isPublished = completed.length == totalPages.length && percentage == 100; 

  const [showPublishShare, setShowPublishShare] = useState(false);

  const renderStatus = (data) => {
    let status = null;
      if(aliasurl && data && !data.published && !data.publishing){
      status = 'URL already exists';
      return status;
    }
      else if (data && !data.published && !data.publishing) {
      aliasurl=null;
      status = 'waiting';
      return status;
    } else if (data && data.error) {
      aliasurl=null;
      status = 'error';
      return status;
    } else {
      aliasurl = null;
      status = data.published ? 'published' : 'publishing';
      return status;
    }
  }

  const handlePersonalizedLink = () => {
    if (!workspace || !storyboard){
      return;
    }
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/storyboards/${storyboard.id}/personalization`;
    const win = window.open(url, "_blank");
    win.focus();
  }

  const handleCampaign = () => {
    if (!workspace || !storyboard){
      return;
    }
    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/storyboards/${storyboard.id}/personalization-campaign`;
    const win = window.open(url, "_blank");
    win.focus();
  }


  const handleLiveSiteClicked = () => {
    if (siteUrl) {
      const win = window.open(siteUrl, "_blank");
      win.focus();
      onDiscard()
    }
  }

  const handleUrlCopy = () => {
    navigator.clipboard.writeText(siteUrl).then(
      () => { },
      () => { }
    )
    const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload.social_platform = "Copy Link";
    dispatch(createMixpanelEvent(MIXPANEL_DISTRIBUTION_CLICKED, mixpanelPayload));

    toast(
      <SuccessToast
        heading={`Success`}
        description={`Copied link to clipboard!`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );
  }

  const handleSocialShare = (type) => {
    const socialPlatformMapping = {
      [socialShareTypes.EMAIL]: 'E-Mail',
      [socialShareTypes.LINKEDIN]: 'Linkedin',
      [socialShareTypes.TWITTER]: 'X',
      [socialShareTypes.FACEBOOK]: 'Facebook',
      [socialShareTypes.WHATSAPP]: 'Whatsapp',
    };
    const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type);
    mixpanelPayload.social_platform = socialPlatformMapping[type];
    dispatch(createMixpanelEvent(MIXPANEL_DISTRIBUTION_CLICKED, mixpanelPayload));
    try {
    let utmLink = siteUrl + "?" + new Date().getTime();
    utmLink = encodeURI(utmLink);

    let winTop = (window.screen.height - 500) / 2;
    let winLeft = (window.screen.width - 575) / 1.75;
    let url = '';

    switch (type) {
      case socialShareTypes.EMAIL:
        const body = encodeURIComponent(siteUrl); // Email body
        const mailtoLink = `https://mail.google.com/mail/?view=cm&body=${body}`;
        window.open(mailtoLink, '_blank');
        break;
      case socialShareTypes.FACEBOOK:
        url = `https://www.facebook.com/sharer.php?u=${utmLink}`;
        break;
      case socialShareTypes.LINKEDIN:
        url = `https://www.linkedin.com/shareArticle?mini=true&url=${utmLink}`;
        break;
      case socialShareTypes.TWITTER:
        url = `https://twitter.com/share?url=${utmLink}`;
        break;
      case socialShareTypes.WHATSAPP:
        const message = encodeURIComponent(siteUrl);
        const whatsappURL = `https://web.whatsapp.com/send?text=${message}`;
        window.open(whatsappURL, "_blank"); 
        break;
    }

    window.open(url, 'sharer', 'top=' + winTop + ',left=' + winLeft + ',toolbar=0,status=0,width=' + 500 + ',height=' + 575);
    } catch (err) {
      console.log("Err == ", err);
    }
  }

  const createVersionOnPublish = () => {
    dispatch(invalidateCreateState());
    
    let request = {
      name: storyboard.name,
      storyboardId: storyboard.id,
      published: true
    }
    dispatch(createNewVersion(request));
  }

  const showMobileEditor = () => {
    onDiscard();
    dispatch(switchBreakpoint(editorSession, 'mobile'));
  }

  useEffect(()=>{
    if(isPublished){
      createVersionOnPublish()

      const mixPanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type)

      dispatch(createMixpanelEvent(MIXPANEL_PUBLISH_STORYBOARD, mixPanelPayload))
    }
  },[isPublished])

  return (
    <Modal backdrop='static'
      className={styles.publishChangeModal}
      contentClassName={styles.publishChangeContent}
      dialogClassName={styles.publishChangeDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.publishChangeBody}>
        <section className={cx(styles.messageRow, {[styles.messageRowSpatial]: SLIDE_STORYBOARDS.includes(storyboard?.type)})}>
          <div className={cx(styles.messageContainer, {[styles.messageContainerSpatial]: SLIDE_STORYBOARDS.includes(storyboard?.type) , [styles.messageContainerHeight]: passwordProtected})}>
            <div>
            <h2 className={styles.messageTitle}>Your site is Published and live.</h2>
              {/* <span className={styles.messageParagraph}>Lorem ipsum dolor sit amet, consectetur elit,
              sed do eiusmod tempor incididunt.</span> */}
            </div>
            {hasVariableMappings &&
              <div className={styles.personalisationContainer}>
                <label className={styles.personalisationLabel}>Looks like you have variables set to your editor</label>
                <div className={styles.buttonWrapper}>
                  <Button  variant="primary" onClick={handlePersonalizedLink} >
                    <i className={cx('cleverstory-personalized-link')} />
                    Create Personalized Link
                  </Button>
                  <Button  variant="primary" onClick={handleCampaign} >
                    <i className={cx('cleverstory-personalization-campaign')} />
                    Create Campaign
                  </Button>
                </div>
              </div>
            }
            {/** Site URL */}
            <div className={styles.liveSiteFormContainer}>
              <label className={styles.formLabel}>Site URL</label>
              <div className={styles.liveSiteFormInputWrapper}>
                <label>{siteUrl}</label>
                <Tooltip placement="top" content={siteUrl}>
                  <span onClick={handleLiveSiteClicked} className={styles.inputIcon}>
                    <i className={cx('cleverstory-url', styles.copyTextButton)} />
                  </span>
                </Tooltip>
                <Tooltip placement="top" content={"Social Share"}>
                  <span onClick={() => {
                    setShowPublishShare(true);
                  }} className={styles.inputIcon}>
                    <i className={cx('cleverstory-share', styles.copyTextButton)} />
                  </span>
                </Tooltip>
              </div>
              {passwordProtected &&
                <div className={styles.protectedStatusWrapper}>
                  <label className={cx(styles.protectedStatus)}>
                    <span>PASSWORD PROTECTED </span>
                    <i className={cx("cleverstory-hide")} /></label>
                </div>
              }
            </div>

            {/** Social Share */}
            {showPublishShare &&
              <div className={styles.socialShareContainer}>
                {/** Copy Link */}
                <Tooltip placement='top' content='Copy Link'>
                  <div className={styles.socialShareWrapper} onClick={handleUrlCopy}>
                    <i className={cx('cleverstory-link', styles.copyTextButton)} />
                    <label className={styles.socialShareLabel}>{"Copy Link"}</label>
                  </div>
                </Tooltip>
                {/** Copy Embed */}
                {!SLIDE_STORYBOARDS.includes(storyboard.type) &&
                  <CopyEmbedCodeButton id={pageId} domain={domain} height={815} socialShare={true}>
                    <Tooltip placement='top' content='Copy Embed Code'>
                      <div className={styles.socialShareWrapper}>
                        <i className={cx('cleverstory-Embed', styles.copyTextButton)} />
                        <span className={cx(styles.socialShareLabel)}>{`Embed`}</span>
                      </div>
                    </Tooltip>
                  </CopyEmbedCodeButton>
                }
                {/** Social Platform */}
                {socialShareOptions.map((data) => (
                  <Tooltip placement='top' content={data.label}>
                    <div key={data.key} className={styles.socialShareWrapper} onClick={() => handleSocialShare(data.id)}>
                      <i className={cx(data.icon, styles.copyTextButton)} />
                        <label className={styles.socialShareLabel}>
                          {data.label}
                        </label>
                    </div>
                  </Tooltip>
                ))}
              </div>
            }
            <div className={styles.progressLoader}>
              <div className={styles.progressDetails}>
                <span className={styles.progressData}>Published {completed.length}/{totalPages.length}</span>
                <span className={styles.status}>{percentage}% <span>|</span> Failed: {errorPage.length}</span>
              </div>
              <ProgressBar className={styles.progressBar} variant="warning" now={percentage} />
            </div>
            <div className={cx(styles.progressPages, {[styles.minimize]: layoutChangeIndicator, [styles.progressPagesPadding]: totalPages?.length > 3 })}>
              {
                totalPages.map((data, index) => (
                  <div key={data.id} className={styles.pageLoader}>
                    <div className={styles.pageDetails}>
                      <TruncatedTextWithTooltip className={styles.pageName} content={data.name} placement="top" maxWidth={340} />
                      <div className={styles.status}>
                      <span className={cx(styles.pageStatus, { [styles.pageStatusCompleted]: data.published },{ [styles.pageStatusError]: !aliasurl && data.error },{ [styles.pageAliasError]: aliasurl && data.error } )}>
                        {renderStatus(data)}
                      </span>
                      </div>
                    </div>
                    <div className={styles.loadingBar}>
                      <div className={cx(styles.progressSlider, { [styles.progressCompleted]: data.published }, { [styles.progressFailed]: data.error })}></div>
                    </div>
                  </div>
                ))
              }
            </div>
            {layoutChangeIndicator &&
              <div className={styles.warningMsgContainer}>
                <div className={styles.warningMsg}>
                  <i className={cx(styles.icon, 'cleverstory-warning')} />
                  <span>There are changes in the mobile view that you may have not noticed.</span>
                </div>
                <Button disabled={completed.length !== totalPages.length && percentage < 100} variant="info" className={styles.editBtn} onClick={showMobileEditor}>
                  <i className={cx('cleverstory-edit')} />
                </Button>
              </div>
            }
          </div>
          <div className={styles.imageContainer}>
            <Button className={styles.closeButton} variant="info" onClick={() => {
                setShowPublishShare(false);
                onDiscard();
              }}>
              <i className='cleverstory-close' />
            </Button>
            <Image src={'/images/publishLoader.png'} className={styles.publishImage} />
          </div>
          <footer className={styles.saveChangeButtons}>
            <Button disabled={completed.length !== totalPages.length && percentage < 100} variant="primary" onClick={onDiscard} >Done</Button>
          </footer>
        </section>
      </Modal.Body>
    </Modal>
  )
}

export const InteractivePublishComplete = ({ show, onDiscard, siteUrl }) => {

  const handleLiveSiteClicked = () => {
    if (siteUrl) {
      const win = window.open(siteUrl, "_blank");
      win.focus();
      onDiscard()
    }
  }

  const handleUrlCopy = () => {
    navigator.clipboard.writeText(siteUrl).then(
      () => {

      },
      () => {

      }
    )
  }

  return (
    <Modal backdrop='static'
      className={styles.publishChangeModal}
      contentClassName={styles.publishChangeContent}
      dialogClassName={styles.publishChangeDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.publishChangeBody}>
        <Row className={styles.messageRow}>
          <Col className={styles.publishCompleteMessage} xs={7}>
            <h2 className={styles.messageTitle}>It's out for the world to see!</h2>
            <span className={styles.description}>
              Get your happy dance on cause your site is published! To access the live site, head to the site URL below.
            </span>
            <div className={styles.pageDetails}>
              <Form.Group className={styles.liveSiteForm}>
                <Form.Label className={styles.formLabel}>Site URL</Form.Label>
                <InputGroup className="mb-3">
                  <Form.Control className={styles.formInput} value={siteUrl} />
                  <InputGroup.Text onClick={handleUrlCopy} className={styles.inputIcon}><i className={cx('cleverstory-copy', styles.copyTextButton)} /></InputGroup.Text>
                </InputGroup>
              </Form.Group>
              <div className={styles.actionContainer}>
                <label className={cx(styles.protectedStatus, styles.protectedLabel)}>PASSWORD PROTECTED <i className={cx("cleverstory-hide")} /></label>
                <span className={styles.description}>You have published a password protected content.</span>
                <Button className={styles.viewSiteButton} onClick={handleLiveSiteClicked} variant="primary">View Live Site</Button>
              </div>
            </div>
          </Col>
          <Col className={styles.imageContainer} xs={5}>
            <Button className={styles.closeButton} variant="info" onClick={onDiscard}>
              <i className={'cleverstory-close'} />
            </Button>
            <Image src={'/images/publishLoader.png'} className={styles.publishImage} />
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

const Handle = React.forwardRef((props, ref) => {
  return (
    <div className={styles.dragHandle} ref={ref} {...props.listeners}>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
    </div>
  );
});

const HideAction = ({ storyboardType, handlePageVisibility, hidden, homePage, pageId, showButton }) => {
  const hiding = useSelector((state) => _.get(state, `pages.page[${pageId}].hiding`, false));
  if (storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2) {
    if(!showButton) return null
    return (
      <span onClick={handlePageVisibility} className={cx({ ['loading']: hiding })}>
        {hidden ? 'Unhide' : 'Hide'}
      </span>
    )
  } 
  return !(homePage?.id === pageId) && <span onClick={handlePageVisibility}> {hidden ? 'Unhide' : 'Hide'} </span> 
}